import { lazy, Suspense } from "react";
import { Seo } from "@/components/Seo";
import { Section, PageHeader, Eyebrow } from "@/components/Section";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { FlowStrip } from "@/components/landing/FlowStrip";
import { FivePiecesAlternating } from "@/components/landing/FivePiecesAlternating";
import { Mail, ShieldCheck, FileCheck2, CheckCircle2 } from "lucide-react";

const ProductDemo = lazy(() =>
  import("@/components/product-demo/ProductDemo").then((m) => ({ default: m.ProductDemo })),
);

const PHASES = [
  {
    icon: Mail,
    eyebrow: "Step 1 · Your team",
    title: "Create a request in seconds",
    body: "Pick the change type — bank account, contact details, address, written instructions, or custom. Set a risk tier and toggle ID verification. The system delivers the link via email or SMS.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Step 2 · Client",
    title: "Land on a co-branded page",
    body: "Your logo and colours, a clear explanation of what's being asked, and — if required — a one-tap step into your workspace's ConnectID identity check.",
  },
  {
    icon: FileCheck2,
    eyebrow: "Step 3 · Client",
    title: "Submit the change as structured data",
    body: "No more PDFs. The form captures exactly the fields your team needs, validated client-side and stored encrypted.",
  },
  {
    icon: CheckCircle2,
    eyebrow: "Step 4 · Your team",
    title: "Review, comment, complete",
    body: "Verified identity, name-match status and full event timeline are visible at a glance. Reviewers acknowledge; completers close the loop.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How it works"
        description="Four-step flow: staff create a verified request, the client lands on a co-branded page, verifies their identity and submits, then staff review and complete."
        path="/how-it-works"
      />
      <Section>
        <PageHeader
          eyebrow="How it works"
          title="A safe chain of custody, in four steps."
          lede="From the request being created to the change being completed, every event is captured, attributed and timestamped."
        />
        <div
          className="mt-10 rounded-xl border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] p-5 sm:p-6 grid sm:grid-cols-[auto,1fr] gap-5 items-center"
          data-testid="callout-bec-stat"
        >
          <AnimatedNumber
            value="$80k+"
            className="block text-4xl sm:text-5xl font-semibold text-[hsl(var(--brand-forest))] leading-none"
          />
          <div className="text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">
            The median reported cost of a cyber incident for a medium Australian business is now over <strong>$80,000</strong>. The four steps below are designed so a single redirected payment — whether it's a settlement, a refund, a payroll run or a vendor payout — never becomes that incident.
            <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">
              Source: ACSC Annual Cyber Threat Report, 2023–24
            </div>
          </div>
        </div>
        <Suspense
          fallback={
            <div className="mt-10 h-[420px] rounded-2xl bg-[hsl(168,32%,17%)]/5 border border-[hsl(var(--brand-sand))]" />
          }
        >
          <ProductDemo />
        </Suspense>
      </Section>
      <Section className="!pt-0">
        <div className="rounded-2xl border border-[hsl(var(--brand-sand))] bg-white/40 p-6 sm:p-10">
          <div className="mb-10">
            <Eyebrow>The flow at a glance</Eyebrow>
            <p className="mt-2 text-sm text-[hsl(var(--brand-charcoal))]">
              End to end, in under three minutes.
            </p>
          </div>
          <FlowStrip />
        </div>
      </Section>
      <Section className="!pt-0">
        <div className="mb-12">
          <Eyebrow>What you get</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] max-w-2xl">
            Five pieces that make change requests safe.
          </h2>
        </div>
        <FivePiecesAlternating />
      </Section>
      <Section className="!pt-0">
        <Eyebrow>The same flow, in writing</Eyebrow>
        <h2 className="mt-2 mb-6 text-2xl sm:text-3xl font-semibold text-[hsl(var(--brand-charcoal))]">
          Four steps, every time.
        </h2>
        <div className="space-y-px bg-[hsl(var(--brand-sand))] border border-[hsl(var(--brand-sand))] rounded-xl overflow-hidden">
          {PHASES.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-[hsl(var(--brand-bone))] p-6 sm:p-10 grid md:grid-cols-12 gap-6 sm:gap-8 items-start">
                <div className="md:col-span-3">
                  <div className="w-12 h-12 rounded bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-brass))] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <Eyebrow><span className="mt-4 inline-block">{p.eyebrow}</span></Eyebrow>
                </div>
                <div className="md:col-span-9">
                  <h2 className="text-2xl font-semibold text-[hsl(var(--brand-charcoal))]">{p.title}</h2>
                  <p className="mt-3 text-[hsl(var(--brand-slate))] leading-relaxed max-w-2xl">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}
